// src/ui/SafeImg.jsx — an <img> that degrades honestly, and doesn't cry wolf.
//
// ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
// A raw <img> with a dead src renders the browser's torn-paper glyph. Most of
// the app's images had no onError at all, so any URL that didn't resolve looked
// like a rendering bug rather than missing content.
//
// ── AND WHY IT RETRIES ──────────────────────────────────────────────────────
// 🟥 THE MISTAKE THE FIRST VERSION MADE. It latched on the FIRST error and never
//    looked again — so a perfectly good image was replaced, permanently, by an
//    "Image unavailable" box. `onError` does not mean "this URL is dead". It also
//    fires when a request is ABORTED, and requests get aborted constantly in
//    normal life: a hot reload, an error boundary rebuilding the tree, a dropped
//    connection, a tab coming back from sleep. One of those is exactly how a
//    working Firebase Storage URL — one that downloads fine when opened directly
//    — ended up showing as broken in the feed.
//
//    So: two silent retries with a short backoff before any verdict, and the
//    whole verdict resets whenever `src` changes. Only a URL that fails three
//    times in a row gets the placeholder.
//
// ── EXCEPT WHEN THERE IS NOTHING TO RETRY ───────────────────────────────────
// A device-local URI (`file://`, `content://`, `ph://`, `assets-library://`) is
// a path on the phone that posted it. A browser can never fetch one, so there is
// no request worth making and no retry worth waiting for — that says so at once.

import React, { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon } from './icons';

const FETCHABLE = /^(https?:|data:|blob:)/i;
const MAX_RETRIES = 2;
const BACKOFF_MS = 500;

/** A device path the phone can read and a browser never can. */
export const isLocalUri = (u) => typeof u === 'string' && /^(file:|content:|ph:|assets-library:)/i.test(u);

/**
 * @param kind  'tile'   → a captioned placeholder box (post media, covers)
 *              'inline' → render nothing at all (favicons, chips, decoration)
 */
export default function SafeImg({
  src, kind = 'tile', label = 'Image unavailable', className, style, onGone, ...rest
}) {
  const [attempt, setAttempt] = useState(0);
  const [dead, setDead] = useState(false);
  const timer = useRef(null);

  // a new src is a clean slate — never inherit the previous one's verdict
  useEffect(() => {
    setAttempt(0);
    setDead(false);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [src]);

  const usable = typeof src === 'string' && !!src && FETCHABLE.test(src);

  if (!usable || dead) {
    if (kind === 'inline') return null;
    /* 🟥 THREE SILENT ATTEMPTS AND THEN NOTHING. Once `dead` was set the only
       way back was a change of `src` or a reload of the page — so a picture that
       failed during one bad minute stayed a grey box for the rest of the
       session, and the reader's only recourse was to reload the whole tab. That
       is a large part of "the multimedia is always broken".

       🟢 A BUTTON, AND ONLY WHERE ONE CAN HELP. A url the phone alone can open
          is not going to load here however many times it is asked, so that case
          keeps its sentence and gets no button. */
    const localOnly = isLocalUri(src);
    return (
      <span className={`vb-img-gone ${className || ''}`} style={style}>
        <ImageIcon width={18} height={18} />
        <em>{localOnly ? 'Only on the phone that posted it' : label}</em>
        {usable && !localOnly && (
          <button
            type="button"
            className="vb-img-retry"
            onClick={(e) => { e.stopPropagation(); setDead(false); setAttempt((a) => a + 1); }}
          >
            Try again
          </button>
        )}
      </span>
    );
  }

  return (
    <img
      // the key forces a genuinely new request per attempt rather than React
      // reusing the element that just failed
      key={`${src}#${attempt}`}
      src={src}
      alt=""
      className={className}
      style={style}
      {...rest}
      onError={() => {
        if (attempt < MAX_RETRIES) {
          timer.current = setTimeout(() => setAttempt((a) => a + 1), BACKOFF_MS * (attempt + 1));
          return;
        }
        try { console.warn(`[allo-web] image failed after ${MAX_RETRIES + 1} attempts:`, src); } catch {}
        setDead(true);
        onGone?.();
      }}
    />
  );
}
